import axios from "axios";
import { getAccessToken, setAccessToken, clearAccessToken } from "./authTokenMemory";
import { getAuthGeneration, isRefreshPaused } from "./authSessionState";

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // e.g. "https://tu-backend/api"
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing = null;

export const waitForPendingRefresh = async () => {
  if (refreshing) {
    try { await refreshing; } catch { /* Logout must still clear the cookie. */ }
  }
};

export const refreshAccessToken = () => {
  if (isRefreshPaused()) return Promise.reject(new Error("El cierre de sesión está en curso."));
  if (!refreshing) {
    const generation = getAuthGeneration();
    refreshing = api.post("/auth/refresh", {}, { skipAuthRefresh: true })
      .then(({ data }) => {
        if (isRefreshPaused() || generation !== getAuthGeneration()) {
          throw new Error("La sesión cambió durante la renovación.");
        }
        if (!data?.accessToken) throw new Error("Respuesta de autenticación inválida.");
        setAccessToken(data.accessToken);
        return data.accessToken;
      })
      .catch((error) => {
        if (generation === getAuthGeneration() && error.response?.status === 401) clearAccessToken();
        throw error;
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
};

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;

    if (!original || original.skipAuthRefresh || error.response?.status !== 401
        || original._retry || isRefreshPaused()) return Promise.reject(error);
    original._retry = true;
    const generation = getAuthGeneration();
    const access = await refreshAccessToken();
    if (isRefreshPaused() || generation !== getAuthGeneration()) return Promise.reject(error);
    original.headers = original.headers || {};
    original.headers.Authorization = `Bearer ${access}`;
    return api.request(original);
  }
);
